import { createContext, useContext, useState, useEffect, useCallback, Fragment } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { setStudyOwner, getStudyScope, isStudyScopeCurrent } from '@/lib/studyStorage';
import { hydrateAIResult } from '@/lib/aiSession';
import { GOOGLE_SIGN_IN_ENABLED } from '@/lib/authProviders';
import AccountDataSync from '@/components/feature/AccountDataSync';

export interface LocalUser {
  id: string;
  email: string;
  created_at?: string;
  user_metadata: {
    display_name?: string;
    avatar_url?: string;
    picture?: string;
  };
}

export interface LocalSession {
  user: LocalUser;
}

export interface AuthState {
  user: LocalUser | null;
  session: LocalSession | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  authProvider: 'supabase' | null;
  isRehydrated: boolean;
}

interface AuthContextValue extends AuthState {
  signUp: (email: string, password: string) => Promise<{ error: Error | null; requiresEmailConfirmation: boolean }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<{ error: Error | null }>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (password: string) => Promise<{ error: Error | null }>;
  updateProfile: (data: { display_name?: string }) => Promise<{ error: Error | null }>;
  refreshSession: () => Promise<void>;
  setAuthProvider: (provider: 'supabase') => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function mapUser(user: User | null): LocalUser | null {
  if (!user) return null;

  return {
    id: user.id,
    email: user.email || '',
    created_at: user.created_at,
    user_metadata: {
      display_name:
        typeof user.user_metadata?.display_name === 'string'
          ? user.user_metadata.display_name
          : undefined,
      avatar_url:
        typeof user.user_metadata?.avatar_url === 'string'
          ? user.user_metadata.avatar_url
          : undefined,
      picture:
        typeof user.user_metadata?.picture === 'string'
          ? user.user_metadata.picture
          : undefined,
    },
  };
}

function mapSession(session: Session | null): LocalSession | null {
  const user = mapUser(session?.user || null);
  return user ? { user } : null;
}

function asError(error: { message?: string } | null): Error | null {
  return error ? new Error(error.message || 'Supabase request failed.') : null;
}

function appUrl(path = '') {
  const basePath = typeof __BASE_PATH__ === 'string' ? __BASE_PATH__ : '/';
  const normalizedBase = basePath === '/' ? '' : basePath.replace(/\/$/, '');
  return `${window.location.origin}${normalizedBase}${path}`;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    isLoading: true,
    isAuthenticated: false,
    authProvider: 'supabase',
    isRehydrated: false,
  });

  useEffect(() => {
    let mounted = true;
    let revision = 0;
    const applySession = async (session: Session | null) => {
      const update = ++revision;
      const previous = getStudyScope();
      setStudyOwner(session?.user.id || null);
      const owner = getStudyScope();
      // Unmount old screen state before any asynchronous asset hydration.
      if (previous !== owner) setState(current => ({ ...current, isRehydrated: false, isLoading: true }));
      if (!mounted || update !== revision || !isStudyScopeCurrent(owner)) return;
      setState({
        user: mapUser(session?.user || null),
        session: mapSession(session),
        isLoading: false,
        isAuthenticated: Boolean(session?.user),
        authProvider: 'supabase',
        isRehydrated: true,
      });
      // Video archives are optional screen data, not an authentication
      // prerequisite. A blocked browser database must not blank History/chat.
      void hydrateAIResult().catch(() => {
        console.warn('A saved lesson could not be restored on this device.');
      });
    };

    void supabase.auth.getSession().then(({ data, error }) => {
      if (mounted && revision === 0) void applySession(error ? null : data.session);
    }).catch(() => {
      if (mounted && revision === 0) void applySession(null);
    });

    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      void applySession(session);
    });

    return () => {
      mounted = false;
      data.subscription.unsubscribe();
    };
  }, []);

  const signUp = useCallback(async (email: string, password: string) => {
    setState((current) => ({ ...current, isLoading: true }));
    try {
      const { data, error } = await supabase.auth.signUp({ email, password });
      if (!error && data.session) {
        const user = mapUser(data.session.user);
        const session = mapSession(data.session);
        setStudyOwner(data.session.user.id);
        setState({
          user,
          session,
          isLoading: false,
          isAuthenticated: Boolean(user),
          authProvider: 'supabase',
          isRehydrated: true,
        });
        void hydrateAIResult().catch(() => {
          console.warn('A saved lesson could not be restored on this device.');
        });
      } else {
        setState((current) => ({ ...current, isLoading: false }));
      }
      return {
        error: asError(error),
        requiresEmailConfirmation: !error && !data.session,
      };
    } catch {
      setState((current) => ({ ...current, isLoading: false }));
      return {
        error: new Error('Unable to create your account. Please try again.'),
        requiresEmailConfirmation: false,
      };
    }
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    setState((current) => ({ ...current, isLoading: true }));
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setState((current) => ({ ...current, isLoading: false }));
    return { error: asError(error) };
  }, []);

  const signInWithGoogle = useCallback(async () => {
    if (!GOOGLE_SIGN_IN_ENABLED) return { error: new Error('Google sign-in is not available. Please use email and password.') };
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: appUrl() },
    });
    return { error: asError(error) };
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (!error) {
      setStudyOwner(null);
      setState(current => ({ ...current, user: null, session: null, isAuthenticated: false, isLoading: false, isRehydrated: true }));
    }
    return { error: asError(error) };
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: appUrl('/auth/reset-password'),
    });
    return { error: asError(error) };
  }, []);

  const updatePassword = useCallback(async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    return { error: asError(error) };
  }, []);

  const updateProfile = useCallback(async (profile: { display_name?: string }) => {
    const { data, error } = await supabase.auth.updateUser({
      data: profile,
    });
    if (!error && data.user) {
      const user = mapUser(data.user);
      const session = user ? { user } : null;
      setState((current) => ({
        ...current,
        user,
        session,
        isAuthenticated: Boolean(user),
      }));
    }
    return { error: asError(error) };
  }, []);

  const refreshSession = useCallback(async () => {
    setState((current) => ({ ...current, isLoading: true }));
    const { data, error } = await supabase.auth.getSession();
    const session = error ? null : data.session;
    const previous = getStudyScope();
    setStudyOwner(session?.user.id || null);
    const owner = getStudyScope();
    if (previous !== owner) setState(current => ({ ...current, isRehydrated: false }));
    if (!isStudyScopeCurrent(owner)) return;
    setState({
      user: mapUser(session?.user || null),
      session: mapSession(session),
      isLoading: false,
      isAuthenticated: Boolean(session?.user),
      authProvider: 'supabase',
      isRehydrated: true,
    });
    void hydrateAIResult().catch(() => {
      console.warn('A saved lesson could not be restored on this device.');
    });
  }, []);

  const setAuthProvider = useCallback((provider: 'supabase') => {
    setState((current) => ({ ...current, authProvider: provider }));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        signUp,
        signIn,
        signInWithGoogle,
        signOut,
        resetPassword,
        updatePassword,
        updateProfile,
        refreshSession,
        setAuthProvider,
      }}
    >
      {state.isRehydrated && <Fragment key={state.user?.id || 'guest'}><AccountDataSync>{children}</AccountDataSync></Fragment>}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}